import React, { useEffect, useRef, useState } from "react";
import { Database, Loader2, RefreshCw, Trash2, TriangleAlert, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const RagBar = ({ sequence }) => (
  <div className="mt-3 flex gap-1" aria-hidden>
    {sequence.map((r, i) => (
      <span
        key={i}
        className={`h-2 flex-1 rounded-sm ${r === "Green" ? "bg-emerald-500" : r === "Amber" ? "bg-amber-400" : "bg-rose-500"}`}
      />
    ))}
  </div>
);

export default function AdminDemoData() {
  const [data, setData] = useState(null);
  const [sector, setSector] = useState("");
  const [profile, setProfile] = useState("mixed");
  const [busy, setBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // "remove" | "clear"
  const [phrase, setPhrase] = useState("");
  const poll = useRef(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/demo-data");
      setData(d);
      setSector((s) => s || d.sector || d.sectors[0]?.key || "");
      if (d.demo?.profile) setProfile(d.demo.profile);
      return d;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load demo data settings");
      return null;
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(poll.current);
  }, []);

  const running = data?.job?.state === "running";

  useEffect(() => {
    clearInterval(poll.current);
    if (!running) return;
    poll.current = setInterval(async () => {
      const d = await load();
      if (d?.job?.state === "done") toast.success("Demo portfolio loaded");
      if (d?.job?.state === "failed") toast.error(d.job.message);
    }, 2500);
    return () => clearInterval(poll.current);
  }, [running]);

  const call = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(path, body || {});
      await load();
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const loaded = data?.demo?.loaded;
  const counts = data?.demo?.counts || data?.job?.counts;

  return (
    <Shell
      testId="admin-demo-page"
      title="Demo Data"
      subtitle={`Load ${data?.projects_per_load || 10} fully populated, sector-specific projects so you can see how the dashboards, registers and reports behave before you add your own. Everything is removable in one click.`}
    >
      {!data ? (
        <Empty testId="demo-loading" text="Loading…" />
      ) : (
        <div className="space-y-6">
          {running && (
            <div data-testid="demo-progress" className="panel flex items-center gap-3 border-l-4 border-l-[color:var(--brand-primary)] p-4">
              <Loader2 size={18} className="animate-spin text-slate-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">{data.job.message}</p>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-slate-100">
                  <div
                    className="h-full rounded bg-[color:var(--brand-primary)] transition-all"
                    style={{ width: `${Math.round(((data.job.step || 0) / (data.job.total || 1)) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-xs tabular-nums text-slate-500">
                {data.job.step || 0}/{data.job.total || 0}
              </span>
            </div>
          )}

          {loaded && !running && (
            <div data-testid="demo-loaded-banner" className="panel border-l-4 border-l-emerald-500 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Demo portfolio loaded — {data.sectors.find((s) => s.key === data.demo.sector)?.label || data.demo.sector},{" "}
                {data.profiles.find((p) => p.key === data.demo.profile)?.label}
              </p>
              {counts && (
                <p className="mt-1 text-xs text-slate-600">
                  {Object.entries(counts)
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
                    .join(" · ")}
                </p>
              )}
            </div>
          )}

          <div className="panel p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">1. Choose the sector</h3>
            <p className="mt-1 text-sm text-slate-600">
              Project names, frameworks, stages and RAID types all follow the sector you pick. Loading also
              applies that sector&apos;s preset to this workspace — terminology, list values, stage labels and
              framework templates — so the seeded projects line up with your configuration.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.sectors.map((s) => (
                <button
                  key={s.key}
                  data-testid={`demo-sector-${s.key}`}
                  disabled={running}
                  onClick={() => setSector(s.key)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    sector === s.key
                      ? "border-transparent bg-[color:var(--brand-primary)] text-white"
                      : "border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="panel p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">2. Choose the portfolio health</h3>
            <p className="mt-1 text-sm text-slate-600">
              Same {data.projects_per_load} projects, three different delivery realities.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              {data.profiles.map((p) => (
                <button
                  key={p.key}
                  data-testid={`demo-profile-${p.key}`}
                  disabled={running}
                  onClick={() => setProfile(p.key)}
                  className={`rounded-lg border p-4 text-left transition-shadow hover:shadow-md ${
                    profile === p.key ? "border-[color:var(--brand-primary)] ring-2 ring-[color:var(--brand-primary)]" : "border-slate-200"
                  }`}
                >
                  <p className="font-display text-sm font-semibold text-slate-900">{p.label}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">{p.headline}</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">{p.description}</p>
                  <RagBar sequence={p.rag_sequence} />
                </button>
              ))}
            </div>
          </div>

          <div className="panel p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">3. Load, reset or clear</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="demo-load-btn"
                disabled={busy || running || loaded || !sector}
                onClick={() => call("/demo-data/load", { sector, profile }, "Loading started")}
                className="gap-1.5"
                style={{ backgroundColor: "var(--brand-primary)" }}
              >
                <Sparkles size={14} /> Load demo portfolio
              </Button>
              <Button
                variant="outline"
                data-testid="demo-reset-btn"
                disabled={busy || running || !loaded}
                onClick={() => call("/demo-data/reset", { sector, profile }, "Rebuilding the demo portfolio")}
                className="gap-1.5"
              >
                <RefreshCw size={14} /> Reset to this selection
              </Button>
              <Button
                variant="outline"
                data-testid="demo-remove-btn"
                disabled={busy || running || !loaded}
                onClick={() => setConfirmDialog("remove")}
                className="gap-1.5"
              >
                <Trash2 size={14} /> Remove demo data
              </Button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Removing the demo data deletes only the seeded records. Your own{" "}
              {data.own_project_count} project{data.own_project_count === 1 ? "" : "s"} and everything attached to
              them are untouched.
            </p>
          </div>

          <div className="panel border-l-4 border-l-rose-500 p-5">
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-rose-900">
              <TriangleAlert size={16} /> Danger zone — clear the whole workspace
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Permanently deletes <strong>every</strong> project, portfolio, programme, RAID record, plan,
              status report, document, reporting period and saved view in this workspace — demo data{" "}
              <strong>and your own data</strong>. Users, roles, branding, lists and frameworks are kept.
              This cannot be undone.
            </p>
            <Button
              variant="destructive"
              data-testid="demo-clear-all-btn"
              disabled={busy || running}
              onClick={() => { setPhrase(""); setConfirmDialog("clear"); }}
              className="mt-3 gap-1.5"
            >
              <Trash2 size={14} /> Clear all data
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
        <DialogContent data-testid="demo-confirm-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog === "clear" ? "Delete everything in this workspace?" : "Remove the demo data?"}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog === "clear" ? (
                <>
                  You are about to delete all delivery data in this workspace, including any projects you
                  created yourself. There is no undo and no backup. Type{" "}
                  <strong>{data?.clear_all_phrase}</strong> below to confirm.
                </>
              ) : (
                "This deletes the seeded demo projects and all of their records. Anything you created yourself stays exactly as it is."
              )}
            </DialogDescription>
          </DialogHeader>
          {confirmDialog === "clear" && (
            <Input
              data-testid="demo-confirm-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={data?.clear_all_phrase}
              autoFocus
            />
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="demo-confirm-cancel" onClick={() => setConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="demo-confirm-btn"
              disabled={busy || (confirmDialog === "clear" && phrase.trim().toUpperCase() !== data?.clear_all_phrase)}
              onClick={async () => {
                const isClear = confirmDialog === "clear";
                setConfirmDialog(null);
                await call(
                  isClear ? "/demo-data/clear-all" : "/demo-data/remove",
                  isClear ? { confirm: phrase } : {},
                  isClear ? "Workspace cleared" : "Demo data removed",
                );
              }}
            >
              {confirmDialog === "clear" ? "Delete everything" : "Remove demo data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
